import { OfficeIdentityContextResolver, MsalAccountIdentityHints } from './officeIdentityContextResolver';
import { MsalAuthError, MsalAuthErrorCode, MsalAuthService } from './msalAuthService';
import { TitusTokenService } from './titusTokenService';
import { localSettingsService } from '../localSettingsService';
import { AuthenticationMode } from '../serviceContainer';

export class AuthService {
    private authenticationMode: AuthenticationMode = 'Office';
    private msalService?: MsalAuthService;
    private userId?: string;
    private idToken?: string;
    private identityHints?: MsalAccountIdentityHints;

    constructor(
        private readonly identityResolver: OfficeIdentityContextResolver,
        private readonly titusTokenService: TitusTokenService,
    ) {}

    /**
     * Initialises the service, resolves user identity and prepare MSAL.
     */
    public async init(useNaa = true): Promise<void> {
        this.authenticationMode = localSettingsService.getLocalSettings().authenticationMode;

        if (this.authenticationMode == 'Office') {
            if (this.identityHints == undefined) {
                this.identityHints = await this.identityResolver.resolve();
            }

            this.msalService = new MsalAuthService(this.identityHints);
            await this.msalService.init(useNaa);
        }
    }

    /**
     * begin the authentication flow: initialises MSAL.
     */
    public async authenticateUser(): Promise<void> {
        if (this.authenticationMode == 'Windows') {
            this.userId = 'WindowsAccount';
            this.idToken = undefined;
        } else {
            if (!this.msalService) {
                throw new Error('You must initialise before attempting to authenticate a user.');
            }

            try {
                await this.msalService.authenticateUser();
            } catch (error) {
                if (error instanceof MsalAuthError) {
                    if (error.code === MsalAuthErrorCode.ConfigurationError) {
                        // if its a configuration error try without naa as that uses different configuration
                        await this.init(false);
                        await this.msalService.authenticateUser();
                    }
                }
            }
            this.userId = this.msalService.getUserId();

            this.idToken = await this.msalService.getUserIdToken();
        }
    }

    /**
     * Gets an access token from Titus using the current user identity.
     */
    public async getTitusToken(): Promise<string> {
        if (!this.userId) {
            await this.authenticateUser();
        }
        if (!this.userId) {
            throw new Error('There was a problem authenticating the user.');
        }

        return this.titusTokenService.getToken(this.idToken, this.userId);
    }
    /**
     * Gets the current cached Titus token.
     */
    public getCachedTitusToken(userId?: string): string {
        return this.titusTokenService.getCachedToken(userId);
    }

    /**
     * Gets an access token for Microsoft Graph using MSAL.
     */
    public async getGraphToken(): Promise<string> {
        if (this.authenticationMode == 'Office') {
            if (!this.msalService) throw new Error('AuthService not initialised.');
            return this.msalService.getGraphAccessToken();
        } else {
            console.warn('Graph token retrieval is only supported with Office Authentication');
            return '';
        }
    }
}
